import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';

import {
    BodyTextMedium,
    Checkbox,
    ButtonGroup,
    Button,
    HeadingText30,
    HeadingText50,
    CalloutInline,
    InputType,
    Option,
    pxToRem,
} from '@modernhealth/vital-components';

import { useSelector } from 'react-redux';
import { Language } from 'src/modules/languages/models/language';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { getSortedLanguages } from 'src/modules/languages/selectors/languageSelectors';
import {
    renderInputWithFormik,
    renderSelectWithFormik,
} from 'src/modules/dependents/components/FormFields';
import { MHTermsLink } from 'src/modules/components/MHTermsLink';

const typesOfSupport = {
    anxiety: 'Anxiety',
    attention_and_executive_functions: 'Attention and Executive Functions',
    autism_spectrum: 'Autism Spectrum',
    bedwetting_or_toileting_issues: 'Bedwetting or Toileting Issues',
    behavioral_issues: 'Behavioral Issues',
    'confidence_self-esteem': 'Confidence & Self-Esteem',
    depression_or_mood_concerns: 'Depression or Mood Concerns',
    disordered_eating: 'Disordered Eating',
    divorce_or_separation_in_the_family: 'Divorce or Separation in the Family',
    feeding_challenges: 'Feeding Challenges (e.g., picky eating)',
    family_concerns: 'Family Concerns',
    gender_or_sexuality: 'Gender or Sexuality',
    learning_challenges: 'Learning Challenges',
    mental_health_related_to_medical_condition:
        'Mental Health Related to Medical Condition (e.g., epilepsy, chronic medical issue)',
    'obsessive-compulsive_behaviors_or_tic-related_behaviors':
        'Obsessive-Compulsive Behaviors or Tic-Related Behaviors',
    racial_ethnic_identity: 'Racial/Ethnic Identity',
    'sensory-processing_challenges': 'Sensory-Processing Challenges',
    sleep: 'Sleep',
    social_skills: 'Social Skills',
    speech_or_language: 'Speech or Language',
    substance_use: 'Substance Use',
    'suicide_or_self-harm': 'Suicide or Self-Harm',
    trauma_or_grief: 'Trauma or Grief',
    other: 'Other',
};

export interface TherapyFormValues {
    firstName: string;
    lastName: string;
    language: string;
    zipCode: string;
    specialties?: Option[];
    anythingElse: string;
    acceptTerms?: boolean;
}

const formSchema = Yup.object<TherapyFormValues>().shape({
    firstName: Yup.string().trim().required('form.validation.firstName.required'),
    lastName: Yup.string().trim().required('form.validation.lastName.required'),
    language: Yup.string().trim().required('form.validation.language.required'),
    zipCode: Yup.string().trim().required('form.validation.zipCode.required'),
    specialties: Yup.array()
        .of(
            Yup.object<Option>().shape({
                label: Yup.string(),
                value: Yup.string(),
            }),
        )
        .required('form.validation.specialties.required'),
    anythingElse: Yup.string().trim(),
    acceptTerms: Yup.bool(),
});

interface TherapyFormProps {
    onCancel: (buttonCopy: string) => void;
    onSubmit: (formData: TherapyFormValues, buttonCopy: string) => void;
    isLoading: boolean;
    initialValues: TherapyFormValues;
}

export const TherapyForm: FC<TherapyFormProps> = ({
    onCancel,
    onSubmit,
    initialValues,
    isLoading,
}) => {
    const { t } = useTranslation();
    const sortedLanguages = useSelector(getSortedLanguages);
    const submitBtnCopy = t('dependents:form.submit_therapy_request');

    const languageOptions = useMemo(
        () =>
            sortedLanguages.map((language: Language) => ({
                label: language.displayName,
                value: language.value,
            })),
        [sortedLanguages],
    );

    const typeOfSupportOptions = useMemo(
        () =>
            Object.keys(typesOfSupport).map(type => ({
                label: t(`dependents:request_therapy.areas_of_support.${type}`),
                value: typesOfSupport[type],
            })),
        [t],
    );

    return (
        <Container>
            <HeadingText50 marginBottom={24}>{t('dependents:request_therapy.title')}</HeadingText50>
            <BodyTextMedium marginBottom={60} maxWidth="480px">
                {t('dependents:request_therapy.description')}
            </BodyTextMedium>
            <Formik<TherapyFormValues>
                validationSchema={formSchema}
                initialValues={initialValues}
                onSubmit={values => onSubmit(values, submitBtnCopy)}
                validateOnMount
            >
                {props => {
                    const renderSelect = renderSelectWithFormik(props, t);
                    const renderInput = renderInputWithFormik(props, t);

                    return (
                        <>
                            <HeadingText30 marginBottom={18}>
                                {t('dependents:form.dependent_information')}
                            </HeadingText30>
                            {renderInput('firstName')}
                            {renderInput('lastName')}
                            {renderSelect('language', languageOptions)}
                            {renderInput('zipCode')}
                            <HeadingText30 marginTop={18} marginBottom={15}>
                                {t('dependents:form.care_information')}
                            </HeadingText30>
                            {renderSelect('specialties', typeOfSupportOptions, true)}
                            {renderInput('anythingElse', InputType.TextArea)}

                            <CheckboxContainer>
                                <BodyTextMedium bold marginBottom={8} fontColor="black">
                                    {t('shared:modern_health.terms_and_conditions')}
                                </BodyTextMedium>
                                <BodyTextMedium marginBottom={15}>
                                    <Trans
                                        t={t}
                                        i18nKey="dependents:request_therapy.terms_of_use"
                                        components={{
                                            'terms-of-use-link': <MHTermsLink />,
                                        }}
                                    />
                                </BodyTextMedium>
                                <Checkbox
                                    label={t('shared:modern_health.agree_to_terms_of_use')}
                                    onChange={() =>
                                        props.setFieldValue(
                                            'acceptTerms',
                                            !props.values.acceptTerms,
                                        )
                                    }
                                    value={props.values.acceptTerms ?? false}
                                />
                            </CheckboxContainer>
                            <ButtonGroup>
                                <Button
                                    variant="secondary"
                                    busy={isLoading}
                                    onClick={() => onCancel(t('dependents:common.back'))}
                                >
                                    {t('dependents:common.back')}
                                </Button>
                                <Button
                                    variant="primary"
                                    busy={isLoading}
                                    disabled={!props.values.acceptTerms}
                                    onClick={props.submitForm}
                                >
                                    {submitBtnCopy}
                                </Button>
                            </ButtonGroup>
                            <InlineMessageContainer>
                                <CalloutInline variant="info" showIcon={false}>
                                    {t('dependents:request_therapy.notice')}
                                </CalloutInline>
                            </InlineMessageContainer>
                        </>
                    );
                }}
            </Formik>
        </Container>
    );
};

const Container = styled.div({
    width: pxToRem(500),
});

const InlineMessageContainer = styled.div({
    marginTop: pxToRem(32),
});

const CheckboxContainer = styled.div`
    margin: ${pxToRem(45, 0)};
`;
